import React, { memo, useEffect } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert, Spin } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, mobile, tablet } from 'services/ui';
import { Colors, NetworkErrors, UserRole } from 'context';
import { Button, ButtonSizes } from 'atoms';
import { appState, authState, useMySurveys } from 'core';
import { PATHS } from 'router/config';

/**
 * /surveys — the student's pending surveys (spec 0011). One card per pending
 * assignment; filling happens on /surveys/:assignmentId. Students only —
 * mirrors the Invoices page role guard (teacher/methodist/demo → 404).
 */
export const Surveys = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { active_role, isDemoUser } = useReactiveVar(authState);
  const currentState = appState();

  const { surveys, loading, error } = useMySurveys();

  useEffect(() => {
    if (active_role !== UserRole.STUDENT || isDemoUser) {
      appState({
        ...currentState,
        networkError: NetworkErrors.pageIsNotAvailable,
      });
    }
  }, [active_role, currentState, isDemoUser]);

  return (
    <SurveysPageWrapper>
      <SurveysHeader>
        <PageTitle>{t('surveys.title')}</PageTitle>
        <PageInfo>{t('surveys.info')}</PageInfo>
      </SurveysHeader>

      {error ? (
        <Alert type="error" showIcon message={t('surveys.loadError')} />
      ) : loading && !surveys.length ? (
        <SpinWrapper>
          <Spin size="large" />
        </SpinWrapper>
      ) : surveys.length ? (
        <CardsList>
          {surveys.map((survey) => (
            <SurveyCard key={survey.assignmentId}>
              <CardTitle>{survey.title}</CardTitle>
              {survey.description ? <CardDescription>{survey.description}</CardDescription> : null}
              <CardMeta>
                {survey.groupName} · {t('surveys.card.afterLesson', { n: survey.lessonNumber })}
              </CardMeta>
              <TakeButton
                size={ButtonSizes.sm}
                onClick={() => navigate(`${PATHS.surveys}/${survey.assignmentId}`)}>
                {t('surveys.card.take')}
              </TakeButton>
            </SurveyCard>
          ))}
        </CardsList>
      ) : (
        <EmptyState>{t('surveys.empty')}</EmptyState>
      )}
    </SurveysPageWrapper>
  );
});

const SurveysPageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const SurveysHeader = styled.div`
  margin: 32px 0;

  ${tablet} {
    margin: 40px 0 32px 0;
  }

  ${desktop} {
    margin: 54px 0 32px 0;
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 4px 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    margin: 0 0 8px 0;
    ${fontService.h3()}
  }

  ${desktop} {
    ${fontService.h2()}
  }
`;

const PageInfo = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}

  ${tablet} {
    ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  }

  ${desktop} {
    ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.regular })}
  }
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const CardsList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SurveyCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 20px 24px;
  border: 1px solid ${Colors.additional_gray};
  border-radius: 12px;
  background-color: ${Colors.white};
`;

const CardTitle = styled.div`
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.bold })}
  margin-bottom: 4px;
`;

const CardDescription = styled.div`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  margin-bottom: 8px;
  white-space: pre-wrap;
`;

const CardMeta = styled.div`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const TakeButton = styled(Button)`
  width: auto;
  margin-top: 16px;

  ${mobile} {
    width: 100%;
  }
`;

const EmptyState = styled.div`
  width: 100%;
  padding: 48px 0;
  text-align: center;
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;
